import {
  AndroidHaptics,
  impactAsync,
  ImpactFeedbackStyle,
  notificationAsync,
  NotificationFeedbackType,
  performAndroidHapticsAsync,
} from "@-/com.npmjs.expo-haptics";
import { type AudioPlayer, createAudioPlayer } from "@-/com.npmjs.expo-audio";
import onetime from "@-/com.npmjs.onetime";
import { Platform } from "@-/com.npmjs.react-native";
import { getHanCoinSoundSources } from "@-/dev.improve.haniverse.assets.audio.han-coins";

/**
 * Players the clinks rotate through, so a fast stream of landings rarely cuts one off. Each is a
 * native player (an ExoPlayer on Android), so the pool stays small.
 */
const CLINK_PLAYER_COUNT = 3;
/** The last coin of a long run clinks this much higher than the first (varispeed rate). */
const CLINK_PITCH_RISE = 0.5;

type HanCoinPlayers = {
  burst: AudioPlayer;
  clinks: AudioPlayer[];
  depositChime: AudioPlayer;
};

/** Haptics are a nicety: an unavailable engine (web, tests, an old device) is never an error. */
function ignoreFailure(promise: Promise<void>): void {
  promise.catch(() => {});
}

/**
 * The deposit's players, created on the first deposit and kept: each is a native player, and the
 * deposit plays up to a dozen sounds a second. `null` where the sounds are unavailable (web).
 */
const getHanCoinPlayers = onetime((): HanCoinPlayers | null => {
  const sources = getHanCoinSoundSources();
  if (sources === null) {
    return null;
  }
  const create = (source: number, volume: number) => {
    const player = createAudioPlayer(source);
    player.volume = volume;
    // Varispeed: a faster rate also raises the pitch, which is what the rising clinks want.
    player.shouldCorrectPitch = false;
    return player;
  };
  return {
    burst: create(sources.burst, 0.7),
    clinks: Array.from({ length: CLINK_PLAYER_COUNT }, () => create(sources.clink, 0.55)),
    depositChime: create(sources.depositChime, 0.8),
  };
});

function play(player: AudioPlayer, rate = 1): void {
  try {
    player.setPlaybackRate(rate);
    void player.seekTo(0).catch(() => {});
    player.play();
  } catch {
    // A player released by the OS (audio-session reset) stays silent rather than throwing.
  }
}

export function playHanCoinBurstFeedback({ isMuted }: { isMuted: boolean }): void {
  ignoreFailure(
    Platform.OS === "android"
      ? performAndroidHapticsAsync(AndroidHaptics.Confirm)
      : impactAsync(ImpactFeedbackStyle.Medium),
  );
  const players = isMuted ? null : getHanCoinPlayers();
  if (players !== null) {
    play(players.burst);
  }
}

/** One coin landing: a light tick and a clink, both climbing as the run goes on. */
export function playHanCoinLandedFeedback({
  index,
  isMuted,
  total,
}: {
  /** 0-based landing order. */
  index: number;
  isMuted: boolean;
  total: number;
}): void {
  ignoreFailure(
    Platform.OS === "android"
      ? performAndroidHapticsAsync(AndroidHaptics.Clock_Tick)
      : impactAsync(index === total - 1 ? ImpactFeedbackStyle.Rigid : ImpactFeedbackStyle.Light),
  );
  const players = isMuted ? null : getHanCoinPlayers();
  const player = players?.clinks[index % CLINK_PLAYER_COUNT];
  if (player !== undefined) {
    play(player, 1 + (CLINK_PITCH_RISE * index) / Math.max(1, total - 1));
  }
}

/** The deposit completing: a success haptic and the chime. */
export function playHanCoinDepositedFeedback({ isMuted }: { isMuted: boolean }): void {
  ignoreFailure(
    Platform.OS === "android"
      ? performAndroidHapticsAsync(AndroidHaptics.Confirm)
      : notificationAsync(NotificationFeedbackType.Success),
  );
  const players = isMuted ? null : getHanCoinPlayers();
  if (players !== null) {
    play(players.depositChime);
  }
}
